import { useState } from 'react'
import { Cloud, Share2, Zap, Lock, Wifi, BarChart3, CheckCircle2, ArrowRight } from 'lucide-react'

export default function DriveIntegrationPage() {
  const [stats] = useState({
    connectedDrives: 4,
    sharedFiles: 2847,
    syncedGb: 487,
    activeUsers: 234,
  })

  const platforms = [
    {
      name: 'Google Drive',
      emoji: '🔵',
      status: 'Connected',
      color: 'from-blue-600 to-blue-500',
      features: ['Real-time sync', 'Collaborative editing', 'Version history', '100GB capacity'],
      storage: '245 GB / 2 TB',
      files: '1,247 files'
    },
    {
      name: 'Microsoft OneDrive',
      emoji: '☁️',
      status: 'Connected',
      color: 'from-cyan-600 to-cyan-500',
      features: ['SharePoint integration', 'Office editing', 'File versioning', '1TB capacity'],
      storage: '156 GB / 1 TB',
      files: '892 files'
    },
    {
      name: 'Dropbox',
      emoji: '📦',
      status: 'Connected',
      color: 'from-blue-600 to-indigo-500',
      features: ['Smart sync', 'Paper docs', 'File requests', '2TB capacity'],
      storage: '65 GB / 2 TB',
      files: '456 files'
    },
    {
      name: 'AWS S3',
      emoji: '🏗️',
      status: 'Connected',
      color: 'from-orange-600 to-yellow-500',
      features: ['Unlimited storage', 'Lifecycle policies', 'Access control', 'CDN delivery'],
      storage: '21 GB used',
      files: '252 files'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-600 p-8 text-white shadow-2xl border border-sky-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Cloud className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Cloud Drive Integration</h1>
                </div>
                <p className="text-sky-100 text-lg font-semibold">Google Drive, OneDrive, Dropbox & More</p>
                <p className="text-sky-200 text-sm mt-1">Seamless integration with all major cloud storage providers</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.connectedDrives}</div>
                <div className="text-sm text-sky-100">Connected Drives</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Connected Drives', value: stats.connectedDrives, icon: Cloud, color: 'from-blue-600 to-blue-500' },
            { label: 'Shared Files', value: stats.sharedFiles, icon: Share2, color: 'from-green-600 to-green-500' },
            { label: 'Synced GB', value: `${stats.syncedGb} GB`, icon: Zap, color: 'from-purple-600 to-purple-500' },
            { label: 'Active Users', value: stats.activeUsers, icon: Wifi, color: 'from-cyan-600 to-cyan-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Connected Cloud Platforms</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {platforms.map((platform, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className={`h-1 w-full bg-gradient-to-r ${platform.color} rounded-full mb-4 shadow-lg`}></div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`bg-gradient-to-br ${platform.color} p-3 rounded-lg text-2xl`}>
                      {platform.emoji}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-xl">{platform.name}</h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full inline-block mt-1 bg-green-500/20 text-green-300">
                        {platform.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-cyan-400 text-sm font-semibold mb-2">{platform.storage}</p>
                  <p className="text-blue-300 text-sm font-semibold mb-3">{platform.files}</p>
                </div>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {platform.features.map((feature, fidx) => (
                      <span key={fidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <button className={`w-full py-2 px-3 bg-gradient-to-r ${platform.color} rounded-lg text-white text-sm font-bold transition-all`}>
                  Configure
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
